import pull from 'lodash/pull';
import last from 'lodash/last';
import { ComponentFactoryResolver, Injectable, Injector } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { DynamicModalComponent } from './dynamic-modal.component';
import { IModalManagerConfig, MODAL_MANAGER_CONFIG } from './i-modal-manager-config';
import { UiContextService } from '../services/ui-context.service';

export const MODAL = {
  CLOSE_EVENTS: {
    DISMISSED: 'DISMISSED',
    COMMON_CLOSE: 'COMMON_CLOSE'
  }
};

interface IOpenedModal {
  closer: (reason: any) => void;
  hash: string;
}

@Injectable()
export class CieUtilsModalManagerService {

  private _openedModals: IOpenedModal[] = [];
  private _lastScrollPosition?: number = null;
  private _isIosDevice: boolean = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
  private readonly _isUseHash: boolean;

  constructor(
    private _ui: UiContextService,
    private _injector: Injector,
    private _modalProvider: NgbModal
  ) {
    const config = _injector.get<IModalManagerConfig>(MODAL_MANAGER_CONFIG);
    this._isUseHash = config ? config.isUseHash : false;
  }

  public show(component: any,
              componentResolver: ComponentFactoryResolver,
              data: any = null,
              options?: NgbModalOptions,
              callbacks?: { thenFn?: any, catchFn?: any, iosDeviceFn?: any }
  ): Promise<any> {
    if (callbacks && typeof callbacks.iosDeviceFn === 'function') {
      callbacks.iosDeviceFn();
    }

    let hash = null;
    if (this._isUseHash) {
      hash = Math.floor(Math.random() * 10000) + '';

      window.location.hash = '#' + hash;
    }

    if (this._isIosDevice) {
      this._saveScrollPositionBeforeFirstOpen();
    }

    const instance = this._modalProvider.open(DynamicModalComponent, options || {});

    /**Patch original dismiss with wrapper*/
    const originalDismiss = instance.dismiss.bind(instance);
    let dismiss = (reason: any) => {
      if (reason) {
        return originalDismiss(reason);
      }
      return originalDismiss(MODAL.CLOSE_EVENTS.DISMISSED);
    };
    instance.dismiss = dismiss;

    let modal: IOpenedModal = { closer: dismiss, hash };
    this._registerModal(modal);

    Object.assign(instance.componentInstance, {
      data: { ...data, dismiss },
      component,
      componentResolver
    });

    return instance.result
      .then((data) => {
        this._deregisterModal(modal);
        this._revertOpenModalClassIfOtherIsOpened(hash);

        if (callbacks && typeof callbacks.thenFn === 'function') {
          callbacks.thenFn();
        }

        return data;
      })
      .catch((err) => {
        this._deregisterModal(modal);
        this._revertOpenModalClassIfOtherIsOpened(hash);

        if (callbacks && typeof callbacks.catchFn === 'function') {
          callbacks.catchFn();
        }

        throw new Error(JSON.stringify(err));
      });
  }

  public closeAllModals(): void {
    this._openedModals.forEach((modal) => modal.closer(MODAL.CLOSE_EVENTS.COMMON_CLOSE));
    this._openedModals = [];
  }

  public closeLatestIfHashEqual(hash: string): void {
    let modal = last(this._openedModals);
    if (modal && modal.hash === hash) {
      modal.closer('FORCE_CLOSE');
      this._deregisterModal(modal);
    }
  }

  private _registerModal(modal: IOpenedModal): void {
    this._openedModals.push(modal);
  }

  private _deregisterModal(modal: IOpenedModal): void {
    pull(this._openedModals, modal);
  }

  private _revertOpenModalClassIfOtherIsOpened(modalHash: string): void {
    setTimeout(() => {
      // moving back to replace prev hash
      if (window.location.hash === '#' + modalHash) {
        history.back();
      }

      if (this._openedModals.length > 0) {
        document.body.classList.add('modal-open');
        return;
      }

      if (this._lastScrollPosition && this._isIosDevice) {
        window.scrollTo(0, this._lastScrollPosition);
      }

      document.body.style.top = '0px';
      document.body.classList.remove('modal-open');
      this._lastScrollPosition = null;
    });
  }

  private _saveScrollPositionBeforeFirstOpen(): void {
    if (!document.querySelector('.modal-dialog')) {
      this._lastScrollPosition = window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop;
      document.body.style.top = -this._lastScrollPosition + 'px';
    }
  }
}
